import type { Judgement, Millis, Verdict } from './types';
import { crossedMilestone } from './combo-milestones';

export const MULTIPLIER_STEP = 4;
export const MAX_MULTIPLIER = 16;
export const STARTING_LIVES = 3;

export const VERDICTS: readonly Verdict[] = ['perfect', 'good', 'weak', 'flop', 'miss'];

export const BASE_POINTS: Readonly<Record<Verdict, number>> = {
  perfect: 100,
  good: 50,
  weak: 20,
  flop: 0,
  miss: 0,
};

export type VerdictCounts = Readonly<Record<Verdict, number>>;

export interface RunState {
  readonly score: number;
  readonly streak: number;
  readonly maxStreak: number;
  readonly multiplier: number;
  readonly maxMultiplier: number;
  readonly lives: number;
  readonly over: boolean;
  readonly judged: number;
  readonly counts: VerdictCounts;
  readonly timedCount: number;
  readonly errorSumMs: Millis;
  readonly errorAbsSumMs: Millis;
  readonly earlyCount: number;
  readonly lateCount: number;
  readonly milestone: number | null;
}

function extendsStreak(verdict: Verdict): boolean {
  return verdict === 'perfect' || verdict === 'good';
}

function breaksStreak(verdict: Verdict): boolean {
  return verdict === 'flop' || verdict === 'miss';
}

function isTimed(verdict: Verdict): boolean {
  return verdict === 'perfect' || verdict === 'good' || verdict === 'weak';
}

function bump(counts: VerdictCounts, verdict: Verdict): VerdictCounts {
  return {
    perfect: counts.perfect + (verdict === 'perfect' ? 1 : 0),
    good: counts.good + (verdict === 'good' ? 1 : 0),
    weak: counts.weak + (verdict === 'weak' ? 1 : 0),
    flop: counts.flop + (verdict === 'flop' ? 1 : 0),
    miss: counts.miss + (verdict === 'miss' ? 1 : 0),
  };
}

export function multiplierFor(streak: number): number {
  if (streak < MULTIPLIER_STEP) return 1;
  return Math.min(MAX_MULTIPLIER, Math.pow(2, Math.floor(streak / MULTIPLIER_STEP)));
}

export function createRunState(): RunState {
  return {
    score: 0,
    streak: 0,
    maxStreak: 0,
    multiplier: 1,
    maxMultiplier: 1,
    lives: STARTING_LIVES,
    over: false,
    judged: 0,
    counts: { perfect: 0, good: 0, weak: 0, flop: 0, miss: 0 },
    timedCount: 0,
    errorSumMs: 0,
    errorAbsSumMs: 0,
    earlyCount: 0,
    lateCount: 0,
    milestone: null,
  };
}

export function applyJudgement(
  state: RunState,
  judgement: Judgement,
  lengthScale = 1,
  pointsScale = 1,
  safe = false,
): RunState {
  if (state.over) return state;
  const { verdict, deltaMs } = judgement;
  const streak = extendsStreak(verdict)
    ? state.streak + 1
    : breaksStreak(verdict)
      ? 0
      : state.streak;
  const multiplier = multiplierFor(streak);
  const timed = isTimed(verdict);
  const lives = !safe && (verdict === 'miss' || verdict === 'flop') ? state.lives - 1 : state.lives;
  const scale = Number.isFinite(lengthScale) ? Math.max(0.5, lengthScale) : 1;
  const share = Number.isFinite(pointsScale) ? Math.max(0, Math.min(1, pointsScale)) : 1;
  return {
    score: state.score + Math.round(BASE_POINTS[verdict] * multiplier * scale * share),
    streak,
    maxStreak: Math.max(state.maxStreak, streak),
    multiplier,
    maxMultiplier: Math.max(state.maxMultiplier, multiplier),
    lives,
    over: lives <= 0,
    judged: state.judged + 1,
    counts: bump(state.counts, verdict),
    timedCount: state.timedCount + (timed ? 1 : 0),
    errorSumMs: state.errorSumMs + (timed ? deltaMs : 0),
    errorAbsSumMs: state.errorAbsSumMs + (timed ? Math.abs(deltaMs) : 0),
    earlyCount: state.earlyCount + (timed && deltaMs < 0 ? 1 : 0),
    lateCount: state.lateCount + (timed && deltaMs > 0 ? 1 : 0),
    milestone: crossedMilestone(state.streak, streak),
  };
}

export function meanErrorMs(state: RunState): Millis {
  return state.timedCount > 0 ? state.errorSumMs / state.timedCount : 0;
}

export function meanAbsErrorMs(state: RunState): Millis {
  return state.timedCount > 0 ? state.errorAbsSumMs / state.timedCount : 0;
}

export function passed(state: RunState, safe = false): boolean {
  if (state.lives <= 0 || state.over) return false;
  return !safe || state.counts.perfect + state.counts.good + state.counts.weak > 0;
}

export function accuracy(state: RunState): number {
  if (state.judged === 0) return 0;
  let earned = 0;
  for (const verdict of VERDICTS) earned += state.counts[verdict] * BASE_POINTS[verdict];
  return earned / (state.judged * BASE_POINTS.perfect);
}
